import * as React from 'react'

import { Box, Button, Grid, Skeleton, Text, Title } from '@factorialco/deprecated-design-system'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { useHapiElementsContext } from 'modules/ats/components/HapiElementsLoader'
import { ContractCard } from 'modules/ats/components/VonqImportedContracts/ContractCard'
import { resetOrderJourney } from 'modules/ats/lib/vonq'
import type Job from 'modules/ats/store/models/Job'

import { ImportContractSidepanel } from './ImportContractSidepanel'
import { VonqCampaignCheckout } from './VonqCampaignCheckout'
import { useGetImportedContracts } from './useGetImportedContracts'

interface Props {
  job?: Job
}

export const VonqImportedContracts = ({ job }: Props) => {
  const { elementsLoaded } = useHapiElementsContext()
  const { loading, importedContracts } = useGetImportedContracts()
  const [importSidepanelOpen, setImportSidepanelOpen] = React.useState(false)
  const hasCheckout = !!job
  const isLoading = !elementsLoaded || loading

  useEffect(() => {
    if (!elementsLoaded) {
      return
    }

    window.hapi.contract.service?.createContract.onSuccess(() => {
      setImportSidepanelOpen(false)
    })

    return () => {
      resetOrderJourney()
    }
  }, [elementsLoaded])

  return (
    <>
      <Box gap="s24" borderRadius={{ all: 'abs008' }}>
        <Title variant="500" title={i18n.t('ats.vonq.contracts_list.title')}>
          <Box flexDirection="row" gap="s8">
            {hasCheckout ? (
              isLoading ? (
                <Skeleton width="s80" height="s16" />
              ) : (
                <VonqCampaignCheckout job={job} />
              )
            ) : null}
            {isLoading ? (
              <Skeleton width="s120" height="s16" />
            ) : (
              <Button type="secondary" onClick={() => setImportSidepanelOpen(true)} iconName="add">
                {i18n.t('ats.vonq.contracts_list.import_contract')}
              </Button>
            )}
          </Box>
        </Title>

        {!isLoading && importedContracts.length === 0 && (
          <Box alignItems="center" paddingY="s32" gap="s4">
            <Text.Normal>{i18n.t('ats.vonq.contracts_list.empty_state.title')}</Text.Normal>
            <HelpCenterLink name="ats_import_job_contracts" external>
              {i18n.t('ats.vonq.contracts_list.empty_state.cta')}
            </HelpCenterLink>
          </Box>
        )}

        <Grid variant="fixed">
          {isLoading
            ? [1, 2, 3, 4].map((index) => (
                <Box
                  justifyContent="spaceBetween"
                  flexGrow
                  gap="s16"
                  background="grey000White"
                  border={{
                    all: {
                      color: 'grey400',
                      width: 's1',
                      style: 'solid',
                    },
                  }}
                  borderRadius={{ all: 'abs008' }}
                  key={`skeleton-${index}`}
                  maxWidth="s300"
                  gridColSpan={{ s: 6, m: 3 }}
                >
                  <Box background="grey200" width="full" height="s120" />
                  <Box gap="s24" padding="s16">
                    <Box gap="s8">
                      <Skeleton width="s240" height="s12" />
                      <Skeleton width="s140" height="s12" />
                    </Box>

                    <Skeleton width="s140" height="s24" />
                  </Box>
                </Box>
              ))
            : importedContracts.map((contract) => (
                <ContractCard
                  key={contract.contract_id}
                  contract={contract}
                  hasCheckout={hasCheckout}
                />
              ))}
        </Grid>
      </Box>

      {importSidepanelOpen && (
        <ImportContractSidepanel onClose={() => setImportSidepanelOpen(false)} />
      )}
    </>
  )
}
